// Thin client for the platform API, which serves the product switcher's
// GET /v1/me/apps. It has to be called on platform's own API host: only
// there does the gateway check the JWT and forward the caller's organisation,
// which the accounts host never does. Degrades to null when
// env.platformApiBaseUrl is unconfigured (k3d, local) and the switcher falls back.

import { queryOptions, useQuery } from "@tanstack/react-query";
import { env } from "@/lib/env";
import { parseErrorResponse } from "./transport";

export interface MeApp {
    id: string;
    name: string;
    /** Absolute URL the user is sent to on select. */
    url: string;
    description: string;
    /** OKLCH hue string from the product catalog. Optional. */
    hue?: string;
}

export interface MeAppsResponse {
    apps: MeApp[];
}

export async function fetchMeApps(): Promise<MeAppsResponse | null> {
    if (!env.platformApiBaseUrl) return null;
    const res = await fetch(`${env.platformApiBaseUrl}/v1/me/apps`, {
        credentials: "include",
        headers: { Accept: "application/json" },
    });
    if (!res.ok) throw await parseErrorResponse(res);
    return (await res.json()) as MeAppsResponse;
}

export const meAppsQueryOptions = queryOptions({
    queryKey: ["platform", "me-apps"],
    queryFn: fetchMeApps,
    // The apps list rarely changes mid-session.
    staleTime: 60 * 60 * 1000,
    enabled: Boolean(env.platformApiBaseUrl),
    // The switcher is optional chrome: a failure falls back to the static row
    // at once, with no retries and no SSO round-trip.
    retry: false,
    meta: { reauth: false },
});

export function useMeApps() {
    return useQuery(meAppsQueryOptions);
}
